// Sales-bucket Order Intake panel — the unified host for the three
// role-scoped variants of the order-intake flow. Replaces the
// pre-2026-06-15 Mfg-bucket `mfgOrderIntake` mode (the bucket move per
// docs/products/turbo_sales.md §5 + handoff §8).
//
// Variants (one mode, three views, picked by the current user's role
// tokens):
//   - Sales-rep view: in-person lead-capture / "submit as deal".
//     Lightweight form → OI-2 `create` / `dealer-create` (turbo_sales.md §11.4).
//   - Ops-reviewer view: paperwork-check / accept-hold-cancel
//     decision per turbo_sales.md §5 item 4 (OpsReviewerIntakePanel).
//   - Mfg-accepter view: the existing accept-to-pipeline /
//     assign-plant / materialize-unit UI. Today: full Mfg-bucket
//     `ManufacturingOrderIntakePanel` content, unchanged.
//
// Variant resolution + accessibility list lives in salesIntakeVariant.ts;
// the variant-switcher tab strip below shows only the variants the
// current user passes. Multi-variant users (small orgs where one person
// wears every hat) get the toggle; single-variant users see the panel
// directly.

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useAnchorEntitlements } from "@/contexts/AnchorEntitlementsContext";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { peekLeadIntakePrefill } from "@/lib/sales-intake/leadIntakePrefill";
import { ManufacturingOrderIntakePanel } from "@/components/manufacturing/ManufacturingOrderIntakePanel";
import { SalesRepIntakeForm } from "./SalesRepIntakeForm";
import { OpsReviewerIntakePanel } from "./OpsReviewerIntakePanel";
import {
  SALES_INTAKE_VARIANT_LABELS,
  accessibleSalesIntakeVariants,
  defaultSalesIntakeVariant,
  type SalesIntakeVariant,
} from "./salesIntakeVariant";

export function SalesIntakePanel() {
  const auth = useAuth();
  const { anchors } = useAnchorEntitlements();
  const accessible = useMemo(
    () => accessibleSalesIntakeVariants(auth, anchors),
    [auth, anchors],
  );
  const defaultVariant = useMemo(
    () => defaultSalesIntakeVariant(auth, anchors),
    [auth, anchors],
  );
  const [variant, setVariant] = useState<SalesIntakeVariant>(() => {
    if (peekLeadIntakePrefill() && accessible.includes("sales_rep")) return "sales_rep";
    if (accessible.includes(defaultVariant)) return defaultVariant;
    return accessible[0] ?? defaultVariant;
  });

  useEffect(() => {
    if (peekLeadIntakePrefill() && accessible.includes("sales_rep")) {
      setVariant("sales_rep");
      return;
    }
    if (accessible.length === 0) return;
    if (!accessible.includes(variant)) {
      setVariant(accessible.includes(defaultVariant) ? defaultVariant : accessible[0]);
    }
  }, [accessible, defaultVariant, variant]);

  if (accessible.length === 0) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/35 dark:text-amber-100">
        Order Intake is empty for your current role or this organization does not
        have Manufacturing. Sales-rep and Ops-reviewer views need their role tokens;
        Manufacturer view also needs the org manufacturing mode.
      </div>
    );
  }

  return (
    <div
      className="w-full text-left space-y-4"
      {...{ [FIREFLY_TARGET_ATTR]: "intake-surface" }}
    >
      {accessible.length > 1 ? (
        <div {...{ [FIREFLY_TARGET_ATTR]: "intake-variant-picker" }}>
          <SalesIntakeVariantTabs
            variants={accessible}
            activeVariant={variant}
            onChange={setVariant}
          />
        </div>
      ) : null}

      {variant === "mfg_accepter" ? (
        <ManufacturingOrderIntakePanel />
      ) : variant === "sales_rep" ? (
        <SalesRepIntakeForm />
      ) : (
        <OpsReviewerIntakePanel />
      )}
    </div>
  );
}

function SalesIntakeVariantTabs(props: {
  variants: SalesIntakeVariant[];
  activeVariant: SalesIntakeVariant;
  onChange: (v: SalesIntakeVariant) => void;
}) {
  const { variants, activeVariant, onChange } = props;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 dark:border-zinc-700 pb-2">
      <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400 mr-2">
        View as
      </span>
      {variants.map((v) => {
        const isActive = v === activeVariant;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            className={
              "rounded-full px-3 py-1 text-xs font-medium transition-colors " +
              (isActive
                ? "bg-indigo-600 text-white"
                : "bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700")
            }
            aria-pressed={isActive}
          >
            {SALES_INTAKE_VARIANT_LABELS[v]}
          </button>
        );
      })}
    </div>
  );
}
